import { FC, useContext, useEffect, useState, useRef, useCallback } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { isLeft } from "fp-ts/lib/Either";
import { useForm } from "react-hook-form";
import LocalizationContextType from "../../../../../domain/providers/localization/LocalizationContextType";
import ModalsContextType from "../../../../../domain/providers/modal/ModalsContextType";
import LocalizationContext from "../../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../../utils/KeyWordLocalization";
import Validators from "../../../../utils/Validators";
import CreateStablishmentComponentProps from "./create_stablishment.component.props";
import CreateStablishmentUseCase, { CreateStablishmentUseCaseName } from "../../../../../domain/use_cases/stablishment/CreateStablishmentUseCase";
import di from "../../../../../DependencyInjection";
import StablishmentEntity from "../../../../../domain/entities/StablishmentEntity";
import UpdateStablishmentUseCase, { UpdateStablishmentUseCaseName } from "../../../../../domain/use_cases/stablishment/UpdateStablishmentUseCase";
import ButtonComponent from "../../../../components/button/ButtonComponent";
import EntitiesConstants from "../../../../utils/EntitiesConstants";
import ScheduleEntity from "../../../../../domain/entities/ScheduleEntity";
import ScheduleManagement from "./schedule_management.component";
import SearchUsersUseCase, { SearchUsersUseCaseName } from "../../../../../domain/use_cases/user/SearchUsersUseCase";
import UserEntity, { UserType } from "../../../../../domain/entities/UserEntity";
import { AutoCompleteItem } from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import AutoCompleteWithCheckComponent from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckComponent";

declare global {
  interface Window {
    google: any;
  }
}

const CreateStablishmentComponent: FC<CreateStablishmentComponentProps> = ({
  onCreated,
  onUpdated,
  stablishment,
}) => {
  const formInfo = useForm();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = formInfo;
  const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
  const { addToast } = useContext(ModalsContext) as ModalsContextType;
  const [loading, setLoading] = useState<boolean>(false);
  const [showMap, setShowMap] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [schedules, setSchedules] = useState<ScheduleEntity[]>([]);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  // Observar cambios en latitud y longitud
  const latValue = watch(EntitiesConstants.StablishmentEntityLatitude);
  const lngValue = watch(EntitiesConstants.StablishmentEntityLongitude);

  // Función para buscar supervisores
  const _onSearchSupervisors = async (query: string): Promise<AutoCompleteItem<UserEntity>[]> => {
    const response = await di.get<SearchUsersUseCase>(SearchUsersUseCaseName).call({
      query,
      page: 1,
      itemsPerPage: 20,
      role: UserType.SUPERVISOR,
    });
    if (isLeft(response)) return [];
    return response.right.data.map((item) => ({
      label: `${item.name} - ${item.email || 'Sin email'}`,
      id: item.id,
      aditionalValue: item,
    }));
  };

  // Función para manejar cambio de supervisor
  const handleChangeSupervisor = (item: AutoCompleteItem<UserEntity>) => {
    console.log("selected supervisor", item);
    setValue("_"+EntitiesConstants.StablishmentEntitySupervisorId, item ? [item] : null);
    setValue(EntitiesConstants.StablishmentEntitySupervisorId, item ? item.id : null);
  };

  const _handleCreateStablishment = async (data: any) => {
    setLoading(true);
    console.log("daata", data);

    const tempStablishment: StablishmentEntity = {
      id: data[EntitiesConstants.StablishmentEntityId] || "",
      name: data[EntitiesConstants.StablishmentEntityName],
      address: data[EntitiesConstants.StablishmentEntityAddress],
      latitude: data[EntitiesConstants.StablishmentEntityLatitude] ? parseFloat(data[EntitiesConstants.StablishmentEntityLatitude]) : undefined,
      longitude: data[EntitiesConstants.StablishmentEntityLongitude] ? parseFloat(data[EntitiesConstants.StablishmentEntityLongitude]) : undefined,
      phone: data[EntitiesConstants.StablishmentEntityPhone],
      type: data[EntitiesConstants.StablishmentEntityType],
      email: data[EntitiesConstants.StablishmentEntityEmail],
      managerName: data[EntitiesConstants.StablishmentEntityManagerName],
      supervisor: data["_"+EntitiesConstants.StablishmentEntitySupervisorId]?.[0]?.aditionalValue || undefined,
      schedules: schedules
    }

    if(schedules.length === 0) {
      addToast(i18n("Debe agregar al menos un horario"), "error", undefined);
      setLoading(false);
      return;
    }
    
    const response = await di
      .get<CreateStablishmentUseCase>(CreateStablishmentUseCaseName)
      .call(tempStablishment);

    if (isLeft(response)) {
      addToast(i18n("Error al crear establecimiento"), "error", undefined);
      setLoading(false);
      return;
    }

    addToast(i18n("Establecimiento creado exitosamente"), "success", undefined);
    onCreated?.(response.right);
    setLoading(false);
  };

  const onDeleteSchedule = async (deletedScheduleId: string) => {
    if (stablishment && deletedScheduleId) {
      stablishment.schedules = stablishment.schedules?.filter(sch => sch.id !== deletedScheduleId) || [];
    }
  }

  const _loadInfo = async () => {
    if (stablishment == null) return;
    setValue(EntitiesConstants.StablishmentEntityName, stablishment.name);
    setValue(EntitiesConstants.StablishmentEntityAddress, stablishment.address);
    setValue(EntitiesConstants.StablishmentEntityLatitude, stablishment.latitude?.toString() || "");
    setValue(EntitiesConstants.StablishmentEntityLongitude, stablishment.longitude?.toString() || "");
    setValue(EntitiesConstants.StablishmentEntityPhone, stablishment.phone);
    setValue(EntitiesConstants.StablishmentEntityType, stablishment.type);
    setValue(EntitiesConstants.StablishmentEntityEmail, stablishment.email);
    setValue(EntitiesConstants.StablishmentEntityManagerName, stablishment.managerName || "");
    setSchedules(stablishment.schedules || []);
    
    // Cargar supervisor si existe
    if (stablishment.supervisor) {
      setValue(EntitiesConstants.StablishmentEntitySupervisorId, stablishment.supervisor.id);
      const auu:AutoCompleteItem<UserEntity> = {
        label: stablishment.supervisor.name,
        id: stablishment.supervisor.id,
        aditionalValue: stablishment.supervisor
      };
      setValue("_"+EntitiesConstants.StablishmentEntitySupervisorId, [auu]);
    }
  };

  const _handleOnEdit = async (data: any) => {
    if (!stablishment) return;
    setLoading(true);

    const tempStablishment: StablishmentEntity = {
      id: stablishment.id,
      name: data[EntitiesConstants.StablishmentEntityName],
      address: data[EntitiesConstants.StablishmentEntityAddress],
      latitude: data[EntitiesConstants.StablishmentEntityLatitude] ? parseFloat(data[EntitiesConstants.StablishmentEntityLatitude]) : undefined,
      longitude: data[EntitiesConstants.StablishmentEntityLongitude] ? parseFloat(data[EntitiesConstants.StablishmentEntityLongitude]) : undefined,
      phone: data[EntitiesConstants.StablishmentEntityPhone],
      type: data[EntitiesConstants.StablishmentEntityType],
      email: data[EntitiesConstants.StablishmentEntityEmail],
      managerName: data[EntitiesConstants.StablishmentEntityManagerName],
      supervisor: data["_"+EntitiesConstants.StablishmentEntitySupervisorId]?.[0]?.aditionalValue || undefined,
      schedules: schedules
    }

    const response = await di
      .get<UpdateStablishmentUseCase>(UpdateStablishmentUseCaseName)
      .call(tempStablishment);

    if (isLeft(response)) {
      addToast(i18n("Error al actualizar establecimiento"), "error", undefined);
      setLoading(false);
      return;
    }

    addToast(i18n("Establecimiento actualizado exitosamente"), "success", undefined);
    onUpdated?.(response.right);
    setLoading(false);
  };

  // Función para verificar si las coordenadas son válidas
  const hasValidCoordinates = useCallback(() => {
    const lat = parseFloat(latValue);
    const lng = parseFloat(lngValue);
    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }, [latValue, lngValue]);

  // Función para inicializar el mapa de solo lectura
  const initializeReadOnlyMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps || !hasValidCoordinates()) {
      return;
    }

    const lat = parseFloat(latValue);
    const lng = parseFloat(lngValue);
    const center = { lat, lng };

    try {
      // Limpiar mapa anterior si existe
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current = null;
      }

      // Crear mapa de solo lectura
      const map = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 15,
        disableDefaultUI: true, // Deshabilitar controles
        gestureHandling: 'none', // Deshabilitar gestos
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        clickableIcons: false, // Deshabilitar iconos clickeables
      });

      mapInstanceRef.current = map;

      // Crear marcador
      const marker = new window.google.maps.Marker({
        position: center,
        map: map,
        title: i18n("Ubicación del establecimiento"),
        clickable: false, // Deshabilitar click en el marcador
      });

      markerRef.current = marker;
      setMapLoaded(true);

    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }, [latValue, lngValue, hasValidCoordinates, i18n]);

  // Función para esperar a que Google Maps esté disponible
  const waitForGoogleMaps = useCallback(() => {
    return new Promise<void>((resolve) => {
      if (window.google?.maps) {
        resolve();
        return;
      }

      const checkGoogle = () => {
        if (window.google?.maps) {
          resolve();
        } else {
          setTimeout(checkGoogle, 100);
        }
      };
      
      checkGoogle();
    });
  }, []);

  // Efecto para mostrar/ocultar el mapa basado en las coordenadas
  useEffect(() => {
    if (hasValidCoordinates()) {
      setShowMap(true);
      
      const initMap = async () => {
        try {
          await waitForGoogleMaps();
          setTimeout(() => {
            initializeReadOnlyMap();
          }, 100);
        } catch (error) {
          console.error('Error loading Google Maps:', error);
        }
      };

      initMap();
    } else {
      setShowMap(false);
      setMapLoaded(false);
    }
  }, [latValue, lngValue, hasValidCoordinates, waitForGoogleMaps, initializeReadOnlyMap]);

  useEffect(() => {
    _loadInfo();
  }, [stablishment]);

  return (
    <form
      className="row"
      onSubmit={handleSubmit(
        stablishment == null ? _handleCreateStablishment : _handleOnEdit
      )}
    >
      <div
        className={`form-group col-md-12 my-3 ${errors[EntitiesConstants.StablishmentEntityName] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityName)}
        </label>
        <input
          type="text"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Nombre del establecimiento")}
          {...register(
            EntitiesConstants.StablishmentEntityName,
            Validators({ required: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityName}
        />
      </div>

      <div
        className={`form-group col-md-12 my-3 ${errors[EntitiesConstants.StablishmentEntityAddress] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityAddress)}
        </label>
        <input
          type="text"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Dirección del establecimiento")}
          {...register(
            EntitiesConstants.StablishmentEntityAddress,
            Validators({ required: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityAddress}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.StablishmentEntityLatitude] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityLatitude)}
        </label>
        <input
          type="number"
          step="any"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Latitud")}
          {...register(EntitiesConstants.StablishmentEntityLatitude, Validators({
            required: true,
            minValue: -90,
            maxValue: 90
          }))} />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityLatitude}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.StablishmentEntityLongitude] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityLongitude)}
        </label>
        <input
          type="number"
          step="any"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Longitud")}
          {...register(EntitiesConstants.StablishmentEntityLongitude, Validators({
            required: true,
            minValue: -180,
            maxValue: 180
          }))}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityLongitude}
        />
      </div>

      {/* Mapa de solo lectura - solo se muestra si hay coordenadas válidas */}
      {showMap && (
        <div className="form-group col-md-12 my-3">
          <label className="form-label">
            {i18n("Vista previa de ubicación")}
          </label>
          <div className="map-preview-container">
            {!mapLoaded && (
              <div className="map-loading-overlay">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{i18n("Cargando mapa...")}</span>
                </div>
                <span className="ms-2">{i18n("Cargando vista previa...")}</span>
              </div>
            )}
            
            <div 
              ref={mapRef} 
              className="map-preview"
              style={{ 
                height: '300px', 
                width: '100%',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                opacity: mapLoaded ? 1 : 0.3,
                transition: 'opacity 0.3s ease'
              }}
            />
            
            {mapLoaded && (
              <small className="text-muted mt-1 d-block">
                {i18n("Vista previa de la ubicación del establecimiento")}
              </small>
            )}
          </div>
        </div>
      )}

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.StablishmentEntityPhone] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityPhone)}
        </label>
        <input
          type="tel"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Teléfono del establecimiento")}
          {...register(
            EntitiesConstants.StablishmentEntityPhone,
            Validators({ required: true, mustBeNumber: true, minLength: 10 })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityPhone}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.StablishmentEntityType] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityType)}
        </label>
        <select
          disabled={loading}
          className="form-control"
          {...register(
            EntitiesConstants.StablishmentEntityType,
            Validators({ required: true })
          )}
        >
          <option value="">{i18n("Seleccionar tipo")}</option>
          <option value="restaurant">{i18n("Restaurante")}</option>
          <option value="cafe">{i18n("Café")}</option>
          <option value="bar">{i18n("Bar")}</option>
          <option value="hotel">{i18n("Hotel")}</option>
          <option value="retail">{i18n("Tienda")}</option>
          <option value="other">{i18n("Otro")}</option>
        </select>
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityType}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.StablishmentEntityEmail] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityEmail)}
        </label>
        <input
          type="email"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Correo del establecimiento")}
          {...register(
            EntitiesConstants.StablishmentEntityEmail,
            Validators({ required: true, email: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityEmail}
        />
      </div>

      <div
        className={`form-group col-md-6 my-3 ${errors[EntitiesConstants.StablishmentEntityManagerName] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(EntitiesConstants.StablishmentEntityManagerName)}
        </label>
        <input
          type="text"
          disabled={loading}
          className="form-control"
          placeholder={i18n("Nombre del encargado")}
          {...register(
            EntitiesConstants.StablishmentEntityManagerName,
            Validators({ required: true, noNumber: true, noSpecialCharacter: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={EntitiesConstants.StablishmentEntityManagerName}
        />
      </div>

      <div className="form-group col-md-6 my-3">
        <AutoCompleteWithCheckComponent
          keyName={"_"+EntitiesConstants.StablishmentEntitySupervisorId}
          label={i18n("Supervisor")}
          formInfo={formInfo}
          multiple={false}
          required
          onSearch={_onSearchSupervisors}
          onChange={handleChangeSupervisor}
        />
        <small className="text-muted">
          {i18n("Busque y seleccione el supervisor del establecimiento")}
        </small>
      </div>

      {/* Sección de Horarios */}
      <div className="col-12 my-4">
        <ScheduleManagement 
          schedules={schedules}
          onSchedulesChange={setSchedules}
          onScheduleDeleted={onDeleteSchedule}
          loading={loading}
        />
      </div>

      <div className="w-100 d-flex justify-content-end my-3">
        {stablishment == null ? (
          <ButtonComponent
            isLoading={loading}
            icon={<span className="material-symbols-outlined">add</span>}
            type="submit"
            design="primaryOutline"
            text={i18n("Guardar")}
          />
        ) : (
          <ButtonComponent
            isLoading={loading}
            icon={<span className="material-symbols-outlined">save</span>}
            type="submit"
            design="primaryOutline"
            text={i18n("Actualizar")}
          />
        )}
      </div>
    </form>
  );
};

export default CreateStablishmentComponent;